import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { isPdfReceipt } from "../../utils/receipts";

/**
 * Read-only view of a single expense, opened by clicking its row.
 *
 * The list can only show a handful of columns — notes in particular were not
 * visible anywhere — so this is where the full record lives, with edit and
 * delete offered from inside it.
 */
export default function ExpenseDetailsDialog({
  expense,
  open,
  onClose,
  onEdit,
  onDelete,
  canModify = false,
  isAdmin = false,
}) {
  const fullScreen = useMediaQuery((t) => t.breakpoints.down("sm"));

  if (!expense) return null;

  const rows = [
    ["Date", expense.date || "-"],
    ["Project", expense.projectName || "-"],
    ["Phase", expense.phaseName || "-"],
    ["Category", expense.category || "-"],
    ...(isAdmin ? [["Submitted by", expense.submittedBy || "-"]] : []),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "flex-start", gap: 1, pr: 1 }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            Expense
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            ${expense.amount}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(96px, auto) 1fr",
            rowGap: 1.25,
            columnGap: 2,
            alignItems: "start",
          }}
        >
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Notes
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {expense.notes || "—"}
        </Typography>

        {expense.receiptUrl && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Receipt
            </Typography>
            <Link
              href={expense.receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none" }}
            >
              {isPdfReceipt(expense.receiptUrl) ? (
                // A PDF cannot be shown inline here, so offer to open it.
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.subtle",
                    color: "text.primary",
                  }}
                >
                  <PictureAsPdfIcon sx={{ fontSize: 32, color: "error.main" }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Receipt PDF
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Opens in a new tab
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={expense.receiptUrl}
                  alt="Receipt"
                  sx={{
                    width: "100%",
                    maxHeight: 280,
                    objectFit: "contain",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.subtle",
                  }}
                />
              )}
            </Link>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          gap: 1,
          flexDirection: { xs: "column-reverse", sm: "row" },
          "& > button": { width: { xs: "100%", sm: "auto" }, ml: { sm: 0 } },
        }}
      >
        <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
        {canModify && (
          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => onDelete(expense)}
          >
            Delete
          </Button>
        )}
        {canModify && (
          <Button
            variant="contained"
            startIcon={<EditOutlinedIcon />}
            onClick={() => onEdit(expense)}
          >
            Edit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
